import { useAtomValue } from 'jotai';
import { BellIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ConditionalRender, Skeleton } from '@crossnokaye/ux-common';

import { EmptyStatePlaceholder } from '@/components/molecules/empty-state-placeholder';
import { AcknowledgedAlarmsTable } from '@/features/alarms/alarm-tables/acknowledged-alarms-table';
import {
  acknowledgedAlarmActivationFilter,
  unacknowledgedAlarmActivationFilter,
} from '@/features/alarms/alarm-tables/helpers';
import { UnacknowledgedAlarmsTable } from '@/features/alarms/alarm-tables/unacknowledged-alarms-table';
import { equipmentLoadingAtom } from '@/features/equipment-browser/equipment-details/state/equipment-response.state';
import { SectionTemplate } from '@/features/equipment-browser/equipment-details/templates/section-template';
import { useDeviceAlarmActivations } from '@/hooks/query-hooks/alarm-activations';

import { EquipmentDetailsAlarmTable } from './equipment-details-alarm-table';
import { EquipmentDetailsAlarmTableBadges } from './equipment-details-alarm-table-badges';
import { AlarmTableProps, AlarmTableView } from './types';

export const EquipmentDetailsAlarmTableSection = ({ deviceId, containerRef }: AlarmTableProps) => {
  const isLoading = useAtomValue(equipmentLoadingAtom);
  const [tableView, setTableView] = useState<AlarmTableView>('unacknowledged-alarms');
  const activeAlarms = useDeviceAlarmActivations({ deviceId });

  const unacknowledgedAlarms = useMemo(
    () => (activeAlarms || []).filter(unacknowledgedAlarmActivationFilter),
    [activeAlarms]
  );
  const acknowledgedAlarms = useMemo(
    () => (activeAlarms || []).filter(acknowledgedAlarmActivationFilter),
    [activeAlarms]
  );

  // Auto-select the view that has items when one is empty and the other isn't
  useEffect(() => {
    if (unacknowledgedAlarms.length === 0 && acknowledgedAlarms.length > 0) {
      setTableView('acknowledged-alarms');
    } else if (acknowledgedAlarms.length === 0 && unacknowledgedAlarms.length > 0) {
      setTableView('unacknowledged-alarms');
    }
  }, [unacknowledgedAlarms.length, acknowledgedAlarms.length]);

  return (
    <>
      <div className="block @2xl:hidden">
        <SectionTemplate title={'Unacknowledged Alarms'} icon={BellIcon} isLoading={isLoading}>
          <ConditionalRender
            condition={!!unacknowledgedAlarms.length}
            fallback={<EmptyStatePlaceholder message="No unacknowledged alarms present" />}
          >
            <UnacknowledgedAlarmsTable
              alarms={unacknowledgedAlarms}
              containerRef={containerRef}
              isLoading={isLoading}
            />
          </ConditionalRender>
        </SectionTemplate>
        <SectionTemplate title={'Acknowledged Alarms'} icon={BellIcon} isLoading={isLoading}>
          <ConditionalRender
            condition={!!acknowledgedAlarms.length}
            fallback={<EmptyStatePlaceholder message="No acknowledged alarms present" />}
          >
            <AcknowledgedAlarmsTable
              alarms={acknowledgedAlarms}
              containerRef={containerRef}
              isLoading={isLoading}
            />
          </ConditionalRender>
        </SectionTemplate>
      </div>
      <div className="hidden @2xl:block">
        <SectionTemplate
          title={'Alarms'}
          icon={BellIcon}
          isLoading={isLoading}
          rightButtons={
            <EquipmentDetailsAlarmTableBadges
              tableView={tableView}
              unacknowledgedCount={unacknowledgedAlarms.length}
              acknowledgedCount={acknowledgedAlarms.length}
              setTableView={setTableView}
            />
          }
        >
          <ConditionalRender condition={!isLoading} fallback={<Skeleton className="w-full h-32" />}>
            <EquipmentDetailsAlarmTable
              deviceId={deviceId}
              containerRef={containerRef}
              tableView={tableView}
            />
          </ConditionalRender>
        </SectionTemplate>
      </div>
    </>
  );
};
